import React, { useRef, useState, useEffect } from 'react';
import * as THREE from 'three';

// Add styles for the drawing canvas overlay
const overlayStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  zIndex: 9999, // Increased z-index to ensure it's above everything
  pointerEvents: 'auto', // Ensure it can receive mouse events
};

const containerStyles: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  maxWidth: '600px',
  width: '90%',
  position: 'relative', // Added position relative
  zIndex: 10000, // Ensure container is above overlay
};

interface DrawingCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  prompt: string;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ isOpen, onClose, onSubmit, prompt }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<'pencil' | 'eraser'>('pencil');
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);
  
  // Initialize canvas
  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    canvas.width = 500;
    canvas.height = 400;
    
    // Clear canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set default stroke style
    ctx.strokeStyle = 'black';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = 5;
  }, [isOpen]);
  
  // Drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    let x, y;
    
    // Handle both mouse and touch events
    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    setIsDrawing(true);
    setLastX(x);
    setLastY(y);
  };
  
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    let x, y;
    
    // Handle both mouse and touch events
    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    
    if (tool === 'pencil') {
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 5;
    } else {
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 20;
    }
    
    ctx.stroke();
    
    setLastX(x);
    setLastY(y);
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="drawing-canvas-overlay" style={overlayStyles}>
      <div className="drawing-canvas-container" style={containerStyles}>
        <h2 style={{ marginBottom: '16px' }}>{prompt}</h2>
        
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
          <button
            style={{
              marginRight: '8px',
              padding: '8px 16px',
              backgroundColor: tool === 'pencil' ? '#3182ce' : '#e2e8f0',
              color: tool === 'pencil' ? 'white' : 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => setTool('pencil')}
          >
            Pencil
          </button>
          <button
            style={{
              marginRight: '8px',
              padding: '8px 16px',
              backgroundColor: tool === 'eraser' ? '#3182ce' : '#e2e8f0',
              color: tool === 'eraser' ? 'white' : 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => setTool('eraser')}
          >
            Eraser
          </button>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={clearCanvas}
          >
            Clear
          </button>
        </div>
        
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '16px',
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseOut={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{ display: 'block', touchAction: 'none' }}
          />
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              marginRight: '8px',
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: '#38a169',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={onSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrawingCanvas; 